import http from '../httpConfig';
import { API_ENDPOINTS } from '../utils/apiConstant';

export interface AccessibleProduct {
  id: number;
  product_code: string;
  product_name: string;
  logo_url: string | null;
  theme_colour: string | null;
}

export interface ProfileData {
  id: number;
  email: string;
  full_name: string | null;
  phone: string | null;
  avatar_url: string | null;
  timezone: string;
  language: string;
  theme: string;
  mfa_enabled: boolean;
  mfa_method: string | null;
  isGlobalProductScope: boolean;
  accessibleProducts: AccessibleProduct[];
}

export interface UpdateProfilePayload {
  full_name?: string;
  phone?: string;
  timezone?: string;
  language?: string;
  theme?: 'light' | 'dark';
}

export interface NotificationPreferences {
  renewal_reminder: boolean;
  receipt: boolean;
  suspension: boolean;
  invite: boolean;
  system: boolean;
}

export interface UserSession {
  id: number;
  jti: string;
  user_agent: string | null;
  ip_address: string | null;
  last_active: string;
  created_at: string;
}

export interface AuditEntry {
  id: number;
  action_type: string;
  action_description: string;
  meta: Record<string, unknown> | null;
  created_at: string;
}

const getProfile = () => http.get(API_ENDPOINTS.myProfile);

const updateProfile = (payload: UpdateProfilePayload) =>
  http.patch(API_ENDPOINTS.myProfile, payload);

const uploadAvatar = (file: File) => {
  const form = new FormData();
  form.append('avatar', file);
  return http.post(API_ENDPOINTS.myAvatar, form);
};

const getPreferences = () => http.get(API_ENDPOINTS.myPreferences);

const updatePreferences = (prefs: Partial<NotificationPreferences>) =>
  http.patch(API_ENDPOINTS.myPreferences, prefs);

const initiateTotpSetup = () => http.post(API_ENDPOINTS.myMfaTotpInitiate, {});

const confirmTotpSetup = (code: string) =>
  http.post(API_ENDPOINTS.myMfaTotpConfirm, { code });

const initiateEmailOtpSetup = () => http.post(API_ENDPOINTS.myMfaEmailOtpInitiate, {});

const confirmEmailOtpSetup = (code: string) =>
  http.post(API_ENDPOINTS.myMfaEmailOtpConfirm, { code });

const disableMfa = () => http.delete(API_ENDPOINTS.myMfa);

const regenerateBackupCodes = () => http.post(API_ENDPOINTS.myMfaBackupCodes, {});

const listSessions = () => http.get(API_ENDPOINTS.mySessions);

const revokeSession = (id: number) =>
  http.delete(`${API_ENDPOINTS.mySession}/${id}`);

const revokeAllSessions = () => http.delete(API_ENDPOINTS.mySessions);

const getActivity = (limit = 20) =>
  http.get(`${API_ENDPOINTS.myActivity}?limit=${limit}`);

const AccountService = {
  getProfile,
  updateProfile,
  uploadAvatar,
  getPreferences,
  updatePreferences,
  initiateTotpSetup,
  confirmTotpSetup,
  initiateEmailOtpSetup,
  confirmEmailOtpSetup,
  disableMfa,
  regenerateBackupCodes,
  listSessions,
  revokeSession,
  revokeAllSessions,
  getActivity,
};

export default AccountService;
